import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const Hyperspeed = ({ effectOptions = {} }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const animationIdRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Default options merged with provided options
    const options = {
      distortion: 'turbulentDistortion',
      length: 400,
      roadWidth: 10,
      islandWidth: 2,
      lanesPerRoad: 4,
      fov: 90,
      fovSpeedUp: 150,
      speedUp: 2,
      carLightsFade: 0.4,
      totalSideLightSticks: 20,
      lightPairsPerRoadWay: 40,
      shoulderLinesWidthPercentage: 0.05,
      brokenLinesWidthPercentage: 0.1,
      brokenLinesLengthPercentage: 0.5,
      lightStickWidth: [0.12, 0.5],
      lightStickHeight: [1.3, 1.7],
      movingAwaySpeed: [60, 80],
      movingCloserSpeed: [-120, -160],
      carLightsLength: [400 * 0.03, 400 * 0.2],
      carLightsRadius: [0.05, 0.14],
      carWidthPercentage: [0.3, 0.5],
      carShiftX: [-0.8, 0.8],
      carFloorSeparation: [0, 5],
      colors: {
        roadColor: 0x080808,
        islandColor: 0x0a0a0a,
        background: 0x000000,
        shoulderLines: 0xFFFFFF,
        brokenLines: 0xFFFFFF,
        leftCars: [0xfbbf24, 0xf59e0b, 0xd97706],
        rightCars: [0x3b82f6, 0x2563eb, 0x1e40af],
        sticks: 0x3b82f6,
      },
      onSpeedUp: () => {},
      onSlowDown: () => {},
      ...effectOptions,
    };

    // Scene setup
    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(options.colors.background, 0, options.length * 0.8);
    sceneRef.current = scene;

    // Camera setup
    const aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
    const camera = new THREE.PerspectiveCamera(options.fov, aspect, 0.1, options.length * 2);
    camera.position.z = 3;
    camera.position.y = 1.5;
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setClearColor(options.colors.background, 1);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create road
    const roadGeometry = new THREE.PlaneGeometry(options.roadWidth, options.length);
    const roadMaterial = new THREE.MeshBasicMaterial({ color: options.colors.roadColor });
    const road = new THREE.Mesh(roadGeometry, roadMaterial);
    road.rotation.x = -Math.PI / 2;
    road.position.z = -options.length / 2;
    scene.add(road);

    // Create island
    const islandGeometry = new THREE.PlaneGeometry(options.islandWidth, options.length);
    const islandMaterial = new THREE.MeshBasicMaterial({ color: options.colors.islandColor });
    const island = new THREE.Mesh(islandGeometry, islandMaterial);
    island.rotation.x = -Math.PI / 2;
    island.position.z = -options.length / 2;
    island.position.y = 0.01;
    scene.add(island);

    // Create light sticks
    const leftSticks = [];
    const rightSticks = [];
    const stickGeometry = new THREE.BoxGeometry(0.1, 1.5, 0.1);
    const stickMaterial = new THREE.MeshBasicMaterial({ color: options.colors.sticks });

    for (let i = 0; i < options.totalSideLightSticks; i++) {
      const leftStick = new THREE.Mesh(stickGeometry, stickMaterial);
      leftStick.position.x = -(options.roadWidth / 2 + 0.5);
      leftStick.position.y = 0.75;
      leftStick.position.z = -(i * (options.length / options.totalSideLightSticks));
      scene.add(leftStick);
      leftSticks.push(leftStick);

      const rightStick = new THREE.Mesh(stickGeometry, stickMaterial);
      rightStick.position.x = options.roadWidth / 2 + 0.5;
      rightStick.position.y = 0.75;
      rightStick.position.z = -(i * (options.length / options.totalSideLightSticks));
      scene.add(rightStick);
      rightSticks.push(rightStick);
    }

    // Create car lights
    const leftCarLights = [];
    const rightCarLights = [];

    for (let i = 0; i < options.lightPairsPerRoadWay; i++) {
      // Left side cars (yellow/orange lights)
      const leftLightGeometry = new THREE.CylinderGeometry(0.08, 0.12, Math.random() * 10 + 5, 8);
      const leftLightMaterial = new THREE.MeshBasicMaterial({
        color: options.colors.leftCars[Math.floor(Math.random() * options.colors.leftCars.length)],
        transparent: true,
        opacity: options.carLightsFade,
      });
      const leftLight = new THREE.Mesh(leftLightGeometry, leftLightMaterial);
      leftLight.rotation.x = Math.PI / 2;
      leftLight.position.x = -(options.roadWidth / 4) + (Math.random() - 0.5) * 2;
      leftLight.position.y = 0.3;
      leftLight.position.z = -(Math.random() * options.length);
      scene.add(leftLight);
      leftCarLights.push({
        mesh: leftLight,
        speed: Math.random() * 20 + 60,
      });

      // Right side cars (blue lights)
      const rightLightGeometry = new THREE.CylinderGeometry(0.08, 0.12, Math.random() * 10 + 5, 8);
      const rightLightMaterial = new THREE.MeshBasicMaterial({
        color: options.colors.rightCars[Math.floor(Math.random() * options.colors.rightCars.length)],
        transparent: true,
        opacity: options.carLightsFade,
      });
      const rightLight = new THREE.Mesh(rightLightGeometry, rightLightMaterial);
      rightLight.rotation.x = Math.PI / 2;
      rightLight.position.x = options.roadWidth / 4 + (Math.random() - 0.5) * 2;
      rightLight.position.y = 0.3;
      rightLight.position.z = -(Math.random() * options.length);
      scene.add(rightLight);
      rightCarLights.push({
        mesh: rightLight,
        speed: -(Math.random() * 40 + 120),
      });
    }

    // Animation
    let time = 0;
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      time += 0.016;

      // Animate light sticks
      [...leftSticks, ...rightSticks].forEach((stick) => {
        stick.position.z += 1;
        if (stick.position.z > 10) {
          stick.position.z = -options.length;
        }
      });

      // Animate car lights
      leftCarLights.forEach((light) => {
        light.mesh.position.z += light.speed * 0.016;
        if (light.mesh.position.z > 10) {
          light.mesh.position.z = -options.length;
          light.mesh.position.x = -(options.roadWidth / 4) + (Math.random() - 0.5) * 2;
        }
      });

      rightCarLights.forEach((light) => {
        light.mesh.position.z += light.speed * 0.016;
        if (light.mesh.position.z < -options.length) {
          light.mesh.position.z = 10;
          light.mesh.position.x = options.roadWidth / 4 + (Math.random() - 0.5) * 2;
        }
      });

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [effectOptions]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    />
  );
};

export default Hyperspeed;
